import { motion } from 'framer-motion';
import { isFrameKey, type FrameKey, type ThemePreset } from '@/lib/themes';
import { SPRING_DEFAULT } from '@/lib/motion';
import { cn } from '@/lib/utils';
import type { Card } from '@/types/database';

// ─── CardHero ─────────────────────────────────────────────────────────────────
// Renders the hero region of a public card: cover image + popped-out profile
// card. The profile card has eight bespoke frame variants matching the
// landing page's marketing gallery, plus a "classic" variant used by the
// legacy color presets (bone/ink/moss/sand/slate) and custom palettes.
//
// Cover image is always rendered as a banner above the card — it's user data,
// independent of frame choice. Each frame controls only the floating card
// region beneath it.
// ──────────────────────────────────────────────────────────────────────────────

type HeroCard = Pick<
  Card,
  | 'slug'
  | 'display_name'
  | 'title'
  | 'company'
  | 'bio'
  | 'photo_url'
  | 'cover_image_url'
  | 'company_logo_url'
  | 'theme_preset'
> &
  Partial<Pick<Card, 'logo_scale'>>;

/** Clamp the user's logo_scale into a sane range. Defaults to 1.0 if missing. */
function resolveLogoScale(scale: number | null | undefined): number {
  const n = typeof scale === 'number' && Number.isFinite(scale) ? scale : 1;
  return Math.min(3, Math.max(0.3, n));
}

interface CardHeroProps {
  card: HeroCard;
  theme: ThemePreset;
  preview?: boolean;
  ownerBackHref?: string;
  compact?: boolean;
}

export function CardHero({ card, theme, preview, ownerBackHref, compact }: CardHeroProps) {
  const fallbackCoverGradient = `linear-gradient(135deg, ${theme.accent} 0%, ${theme.text} 100%)`;
  const heroHeight = compact ? 'aspect-[3/2] max-h-[260px]' : 'aspect-[3/2] max-h-[420px]';
  const cardOverlap = compact ? '-mt-20' : '-mt-32';

  return (
    <>
      {/* Cover banner */}
      <div
        className={cn('relative w-full overflow-hidden', heroHeight)}
        style={
          card.cover_image_url
            ? {
                backgroundImage: `url("${card.cover_image_url}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : { backgroundImage: fallbackCoverGradient }
        }
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,10,0) 30%, rgba(10,10,10,0.32) 100%)',
          }}
        />

        {ownerBackHref && !preview && (
          <a
            href={ownerBackHref}
            className="absolute left-5 top-5 z-10 flex items-center gap-1.5 rounded-full bg-ink/40 px-3 py-1.5 text-[10px] font-medium uppercase tracking-widest text-bone backdrop-blur-md transition-colors hover:bg-ink/60 safe-pt"
          >
            ← Back to edit
          </a>
        )}

        <span
          className={cn(
            'absolute right-5 top-5 z-10 rounded-full bg-ink/30 px-3 py-1.5 text-[10px] font-medium uppercase tracking-widest text-bone backdrop-blur-md safe-pt',
            preview && 'opacity-80',
          )}
        >
          Haphe
        </span>
      </div>

      {/* Floating profile card — frame-specific layout */}
      <div className={cn('relative z-10 mx-auto max-w-md px-5', cardOverlap)}>
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={SPRING_DEFAULT}
        >
          <ProfileFrame card={card} theme={theme} compact={compact} />
        </motion.div>
      </div>
    </>
  );
}

// ─── ProfileFrame dispatch ────────────────────────────────────────────────────
function ProfileFrame({
  card,
  theme,
  compact,
}: {
  card: HeroCard;
  theme: ThemePreset;
  compact?: boolean;
}) {
  if (!isFrameKey(card.theme_preset)) {
    return <ClassicFrame card={card} theme={theme} compact={compact} />;
  }
  switch (card.theme_preset as FrameKey) {
    case 'mono':      return <MonoFrame card={card} theme={theme} compact={compact} />;
    case 'cream':     return <CreamFrame card={card} theme={theme} compact={compact} />;
    case 'grid':      return <GridFrame card={card} theme={theme} compact={compact} />;
    case 'editorial': return <EditorialFrame card={card} theme={theme} compact={compact} />;
    case 'sticker':   return <StickerFrame card={card} theme={theme} compact={compact} />;
    case 'ticket':    return <TicketFrame card={card} theme={theme} compact={compact} />;
    case 'blueprint': return <BlueprintFrame card={card} theme={theme} compact={compact} />;
    case 'neon':      return <NeonFrame card={card} theme={theme} compact={compact} />;
  }
}

// ─── Helpers shared across frames ─────────────────────────────────────────────
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}

function publicHandle(slug: string): string {
  return `gethaphe.com/u/${slug}`;
}

function isLightText(theme: ThemePreset): boolean {
  // Conservative check on the card surface: if the bg is dark, the text is light.
  const cardBg = theme.cardBg;
  if (cardBg.startsWith('#')) {
    const h = cardBg.slice(1);
    if (/^[0-9a-fA-F]{6}$/.test(h)) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55;
    }
  }
  return false;
}

// ─── Classic — photo on the left, identity on the right ───────────────────────
function ClassicFrame({
  card,
  theme,
  compact,
}: {
  card: HeroCard;
  theme: ThemePreset;
  compact?: boolean;
}) {
  const lightText = !isLightText(theme);
  return (
    <div
      className="overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgba(10,10,10,0.45)] card-inner-glow"
      style={{
        backgroundColor: theme.cardBg,
        color: theme.text,
        borderColor: lightText ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.10)',
      }}
    >
      <div className="flex items-stretch">
        {/* Photo column — self-stretches to fill the full card height. */}
        <div
          className="relative w-[45%] shrink-0 self-stretch"
          style={{ backgroundColor: theme.accent }}
        >
          {card.photo_url ? (
            <img
              src={card.photo_url}
              alt={card.display_name}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-2xl font-medium"
              style={{ color: theme.cardBg }}
            >
              {initials(card.display_name)}
            </div>
          )}
        </div>

        {/* Identity column.
            The logo is absolutely positioned at the bottom so its scale
            doesn't push this column's natural height (which used to
            inflate the photo via self-stretch). Reserve enough
            paddingBottom to accommodate the logo at its MAX scale (2x)
            plus the bottom offset — so the logo never grows into the
            text above. At lower scales the logo just sits at the bottom
            of the reserved zone with quiet space above; the card height
            stays stable across the whole slider range. */}
        <div
          className={cn('relative flex min-w-0 flex-1 flex-col p-5')}
          style={
            card.company_logo_url
              ? {
                  // 2 × baseHeight + bottom offset + small buffer.
                  // Regular: 2*40 + 20 + 8 = 108. Compact: 2*36 + 16 + 8 = 96.
                  paddingBottom: compact ? '96px' : '108px',
                }
              : undefined
          }
        >
          <div className="min-w-0">
            <h1
              className={cn(
                'font-semibold leading-[1.05] tracking-tightest',
                compact ? 'text-2xl' : 'text-3xl',
              )}
            >
              {card.display_name || 'Your name'}
            </h1>
            {card.title && (
              <p className="mt-2 text-[13px] font-medium leading-snug opacity-80">{card.title}</p>
            )}
            {card.company && (
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] opacity-65">
                {card.company}
              </p>
            )}
          </div>

          {card.company_logo_url && (
            <div
              className={cn(
                'absolute left-5 right-5 flex items-end',
                compact ? 'bottom-4' : 'bottom-5',
              )}
            >
              <FrameLogo card={card} baseHeightPx={compact ? 36 : 40} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Frame: Monolith — quiet, technical ───────────────────────────────────────
function MonoFrame({ card, theme, compact }: { card: HeroCard; theme: ThemePreset; compact?: boolean }) {
  return (
    <div
      className="overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)]"
      style={{
        backgroundColor: theme.cardBg,
        color: theme.text,
        borderColor: 'rgba(245,242,236,0.18)',
        fontFamily: '"Geist", ui-sans-serif, system-ui, sans-serif',
      }}
    >
      <FrameInnerPad compact={compact}>
        <FrameTopRow>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-55">
            haphe / monolith
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-55">v.01</span>
        </FrameTopRow>

        <PhotoBlock card={card} theme={theme} compact={compact} corners="square" />

        <div className="mt-4">
          <h1
            className={cn(
              'font-medium leading-[1.05] tracking-tightest',
              compact ? 'text-2xl' : 'text-3xl',
            )}
            style={{ color: theme.text }}
          >
            {card.display_name || 'Your name'}
          </h1>
          {(card.title || card.company) && (
            <p className="mt-2 text-[12px] opacity-70">
              {[card.title, card.company].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        <div className="my-4 h-px" style={{ background: 'rgba(245,242,236,0.12)' }} />

        <div className="flex items-end justify-between gap-3">
          <FrameFoot slug={card.slug} theme={theme} />
          <FrameLogo card={card} baseHeightPx={24} align="right" />
        </div>
      </FrameInnerPad>
    </div>
  );
}

// ─── Frame: Cream — warm, hand-feel ───────────────────────────────────────────
function CreamFrame({ card, theme, compact }: { card: HeroCard; theme: ThemePreset; compact?: boolean }) {
  const tags = [card.company, card.title].filter(Boolean).slice(0, 3) as string[];
  return (
    <div
      className="overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgba(10,10,10,0.25)]"
      style={{ backgroundColor: theme.cardBg, color: theme.text, borderColor: 'rgba(10,9,8,0.08)' }}
    >
      <FrameInnerPad compact={compact}>
        <div
          className="mb-4 h-7 w-7 rounded-full"
          style={{ backgroundColor: theme.accent }}
          aria-hidden="true"
        />
        <PhotoBlock card={card} theme={theme} compact={compact} corners="round" />
        <h1
          className={cn(
            'mt-4 italic leading-[1.05]',
            compact ? 'text-3xl' : 'text-4xl',
          )}
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontWeight: 400,
            color: theme.text,
          }}
        >
          {card.display_name || 'Your name'}
        </h1>
        {(card.title || card.company) && (
          <p
            className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em]"
            style={{ color: 'rgba(10,9,8,0.6)' }}
          >
            {[card.title, card.company].filter(Boolean).join(' · ')}
          </p>
        )}
        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className="rounded-full border px-2.5 py-0.5 text-[10px]"
                style={{ borderColor: 'rgba(10,9,8,0.18)' }}
              >
                {t}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <FrameFoot slug={card.slug} theme={theme} />
          <FrameLogo card={card} baseHeightPx={28} align="right" />
        </div>
      </FrameInnerPad>
    </div>
  );
}

// ─── Frame: Grid — schematic, monospace ───────────────────────────────────────
function GridFrame({ card, theme, compact }: { card: HeroCard; theme: ThemePreset; compact?: boolean }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)]"
      style={{
        backgroundColor: theme.cardBg,
        color: theme.text,
        borderColor: 'rgba(245,242,236,0.18)',
      }}
    >
      {/* Grid overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-80"
        style={{
          backgroundImage:
            'linear-gradient(rgba(245,242,236,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(245,242,236,0.10) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <FrameInnerPad compact={compact} className="relative">
        <PhotoBlock card={card} theme={theme} compact={compact} corners="square" />
        <h1
          className={cn(
            'mt-4 leading-[1.05]',
            compact ? 'text-2xl' : 'text-3xl',
          )}
          style={{
            fontFamily: '"Geist Mono", ui-monospace, monospace',
            fontWeight: 400,
            color: theme.text,
          }}
        >
          {nameWithDot(card.display_name, theme.accent)}
        </h1>
        {(card.title || card.company) && (
          <p
            className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ color: theme.accent }}
          >
            {[card.title, card.company].filter(Boolean).join(' · ')} · 2026
          </p>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <FrameFoot slug={card.slug} theme={theme} bracket />
          <FrameLogo card={card} baseHeightPx={24} align="right" />
        </div>
      </FrameInnerPad>
    </div>
  );
}

// ─── Frame: Editorial — serif-led, considered ─────────────────────────────────
function EditorialFrame({
  card,
  theme,
  compact,
}: {
  card: HeroCard;
  theme: ThemePreset;
  compact?: boolean;
}) {
  return (
    <div
      className="overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgba(10,10,10,0.25)]"
      style={{ backgroundColor: theme.cardBg, color: theme.text, borderColor: 'rgba(10,9,8,0.08)' }}
    >
      <FrameInnerPad compact={compact}>
        <p
          className="font-mono text-[11px] uppercase tracking-[0.16em]"
          style={{ color: 'rgba(10,9,8,0.5)' }}
        >
          N° 04
        </p>
        <PhotoBlock card={card} theme={theme} compact={compact} corners="round" className="mt-3" />
        <h1
          className={cn(
            'mt-3 leading-[1.02]',
            compact ? 'text-3xl' : 'text-4xl',
          )}
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontWeight: 400,
            color: theme.text,
          }}
        >
          {card.display_name || 'Your name'}
        </h1>
        {(card.title || card.company) && (
          <p
            className="mt-1 italic"
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: '14px',
              color: 'rgba(10,9,8,0.7)',
            }}
          >
            — {[card.title, card.company].filter(Boolean).join(', ')}
          </p>
        )}
        {card.bio && (
          <p
            className="mt-3 italic"
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: '13px',
              color: 'rgba(10,9,8,0.6)',
              maxWidth: '32ch',
            }}
          >
            "{card.bio}"
          </p>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <FrameFoot slug={card.slug} theme={theme} />
          <FrameLogo card={card} baseHeightPx={28} align="right" />
        </div>
      </FrameInnerPad>
    </div>
  );
}

// ─── Frame: Sticker — loud, friendly ──────────────────────────────────────────
function StickerFrame({
  card,
  theme,
  compact,
}: {
  card: HeroCard;
  theme: ThemePreset;
  compact?: boolean;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)]"
      style={{
        backgroundColor: theme.cardBg,
        color: theme.text,
        border: `2px dashed color-mix(in oklch, ${theme.accent} 60%, transparent)`,
      }}
    >
      {/* Big corner shape */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full"
        style={{ backgroundColor: theme.accent, opacity: 0.85 }}
      />
      <FrameInnerPad compact={compact} className="relative">
        <PhotoBlock card={card} theme={theme} compact={compact} corners="round" />
        <h1
          className={cn(
            'mt-4 font-semibold leading-[1.05] tracking-tightest',
            compact ? 'text-2xl' : 'text-3xl',
          )}
          style={{ color: theme.text }}
        >
          {card.display_name || 'Your name'}
        </h1>
        {(card.title || card.company) && (
          <p className="mt-2 text-[12px] opacity-80">
            {[card.title, card.company].filter(Boolean).join(' · ')}
          </p>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <FrameFoot slug={card.slug} theme={theme} />
          <FrameLogo card={card} baseHeightPx={28} align="right" />
        </div>
      </FrameInnerPad>
    </div>
  );
}

// ─── Frame: Ticket — split with vertical stub ─────────────────────────────────
function TicketFrame({
  card,
  theme,
  compact,
}: {
  card: HeroCard;
  theme: ThemePreset;
  compact?: boolean;
}) {
  // Stable per-card serial from the slug so it doesn't shuffle on render.
  const serial = String(
    Math.abs(
      [...card.slug].reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 7),
    ) % 10000,
  ).padStart(4, '0');

  return (
    <div
      className="grid overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgba(10,10,10,0.25)]"
      style={{
        gridTemplateColumns: '64px 1fr',
        backgroundColor: theme.cardBg,
        color: theme.text,
        border: '1px dashed rgba(10,9,8,0.25)',
      }}
    >
      {/* Stub */}
      <div
        className="flex flex-col items-center justify-between py-4"
        style={{
          background: '#0A0908',
          color: '#F5F2EC',
          borderRight: '2px dashed rgba(245,242,236,0.3)',
          writingMode: 'vertical-rl',
          textAlign: 'center',
        }}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">NO. {serial}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">ADMIT ONE</span>
      </div>

      {/* Body */}
      <div className={cn('flex flex-col', compact ? 'p-4' : 'p-5')}>
        <PhotoBlock card={card} theme={theme} compact={compact} corners="round" />
        <h1
          className={cn(
            'mt-4 font-medium leading-[1.05] tracking-tightest',
            compact ? 'text-2xl' : 'text-3xl',
          )}
          style={{ color: theme.text }}
        >
          {card.display_name || 'Your name'}
        </h1>
        {(card.title || card.company) && (
          <p
            className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em]"
            style={{ color: 'rgba(10,9,8,0.6)' }}
          >
            {[card.title, card.company].filter(Boolean).join(' — ')}
          </p>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <FrameFoot slug={card.slug} theme={theme} />
          <FrameLogo card={card} baseHeightPx={28} align="right" />
        </div>
      </div>
    </div>
  );
}

// ─── Frame: Blueprint — drafted, technical ────────────────────────────────────
function BlueprintFrame({
  card,
  theme,
  compact,
}: {
  card: HeroCard;
  theme: ThemePreset;
  compact?: boolean;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)]"
      style={{ backgroundColor: theme.cardBg, color: theme.text, borderColor: 'rgba(221,235,255,0.18)' }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '16px 16px',
        }}
      />
      <FrameInnerPad compact={compact} className="relative">
        <p
          className="font-mono text-[10px] uppercase tracking-[0.18em]"
          style={{ color: 'rgba(221,235,255,0.6)' }}
        >
          FIG. 04 — CONTACT
        </p>
        <PhotoBlock card={card} theme={theme} compact={compact} corners="square" className="mt-3" />
        <h1
          className={cn(
            'mt-4 leading-[1.05]',
            compact ? 'text-2xl' : 'text-3xl',
          )}
          style={{
            fontFamily: '"Geist Mono", ui-monospace, monospace',
            fontWeight: 400,
            color: theme.text,
          }}
        >
          {card.display_name || 'Your name'}
        </h1>
        {(card.title || card.company) && (
          <p
            className="mt-2 font-mono text-[11px]"
            style={{ color: 'rgba(221,235,255,0.7)' }}
          >
            {[card.title, card.company].filter(Boolean).join(' · ')}
          </p>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <FrameFoot slug={card.slug} theme={theme} bracket />
          <FrameLogo card={card} baseHeightPx={24} align="right" />
        </div>
      </FrameInnerPad>
    </div>
  );
}

// ─── Frame: Neon — late-night ────────────────────────────────────────────────
function NeonFrame({ card, theme, compact }: { card: HeroCard; theme: ThemePreset; compact?: boolean }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgba(0,0,0,0.7)]"
      style={{
        backgroundColor: theme.cardBg,
        color: theme.text,
        borderColor: 'rgba(245,242,236,0.18)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(circle at 30% 50%, color-mix(in oklch, ${theme.accent} 32%, transparent), transparent 60%)`,
        }}
      />
      <FrameInnerPad compact={compact} className="relative">
        <PhotoBlock card={card} theme={theme} compact={compact} corners="square" />
        <h1
          className={cn(
            'mt-4 leading-[1.05] uppercase',
            compact ? 'text-2xl' : 'text-3xl',
          )}
          style={{
            fontFamily: '"Geist Mono", ui-monospace, monospace',
            fontWeight: 500,
            letterSpacing: '0.04em',
            color: theme.text,
          }}
        >
          {card.display_name || 'Your name'}
        </h1>
        {(card.title || card.company) && (
          <p
            className="mt-2 font-mono text-[11px]"
            style={{ color: theme.accent }}
          >
            ▍ {[card.title, card.company].filter(Boolean).join(' · ').toUpperCase()} ▍
          </p>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <FrameFoot slug={card.slug} theme={theme} />
          <FrameLogo card={card} baseHeightPx={24} align="right" />
        </div>
      </FrameInnerPad>
    </div>
  );
}

// ─── Frame primitives ─────────────────────────────────────────────────────────
function FrameInnerPad({
  children,
  compact,
  className,
}: {
  children: React.ReactNode;
  compact?: boolean;
  className?: string;
}) {
  return <div className={cn(compact ? 'p-5' : 'p-6', className)}>{children}</div>;
}

function FrameTopRow({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-between">{children}</div>;
}

function PhotoBlock({
  card,
  theme,
  compact,
  corners,
  className,
}: {
  card: HeroCard;
  theme: ThemePreset;
  compact?: boolean;
  corners: 'round' | 'square';
  className?: string;
}) {
  const size = compact ? 'h-14 w-14' : 'h-16 w-16';
  const radius = corners === 'round' ? 'rounded-full' : 'rounded-md';
  return (
    <div
      className={cn('relative overflow-hidden', size, radius, className)}
      style={{ backgroundColor: theme.accent }}
    >
      {card.photo_url ? (
        <img
          src={card.photo_url}
          alt={card.display_name}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center font-medium"
          style={{ color: theme.cardBg, fontSize: compact ? 16 : 18 }}
        >
          {initials(card.display_name)}
        </div>
      )}
    </div>
  );
}

function FrameFoot({
  slug,
  theme,
  bracket,
  className,
}: {
  slug: string;
  theme: ThemePreset;
  bracket?: boolean;
  className?: string;
}) {
  const label = bracket ? `[ ${publicHandle(slug)} ]` : publicHandle(slug);
  return (
    <div
      className={cn('font-mono text-[10px] uppercase tracking-[0.18em] opacity-55', className)}
      style={{ color: theme.text }}
    >
      {label}
    </div>
  );
}

/**
 * Renders the company logo (if uploaded) at `baseHeightPx`, multiplied by
 * the per-card `logo_scale`. Returns null when there's no logo so the
 * caller can render nothing instead of an empty box.
 */
function FrameLogo({
  card,
  baseHeightPx,
  align = 'left',
  className,
}: {
  card: HeroCard;
  baseHeightPx: number;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  if (!card.company_logo_url) return null;
  const scale = resolveLogoScale(card.logo_scale);
  const height = Math.round(baseHeightPx * scale);
  const objectAlign =
    align === 'right' ? 'object-right' : align === 'center' ? 'object-center' : 'object-left';
  return (
    <img
      src={card.company_logo_url}
      alt={card.company ?? ''}
      className={cn('object-contain', objectAlign, className)}
      // max-width keeps wide wordmarks inside the card column on small frames.
      style={{ height: `${height}px`, maxWidth: '80%' }}
    />
  );
}

/**
 * Renders the display name with an accent-colored separator dot, like
 * "Roger.Carlson". Used by the Grid frame for its schematic look.
 */
function nameWithDot(name: string, accent: string) {
  const parts = name.split(/\s+/);
  if (parts.length < 2) return name;
  const [first, ...rest] = parts;
  return (
    <>
      {first}
      <span style={{ color: accent }}>.</span>
      {rest.join(' ')}
    </>
  );
}
